import React, { useState } from "react";
import Helmet from "react-helmet";
import { Col, Container, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Breadcrumbs, ButtonFix, NavigationBar } from "../../common";
import "./PricesPageLayout.scss";
import { ReactComponent as LockKey } from "../../../assets/icons/lockKey.svg";
import { ReactComponent as CaretLeft } from "../../../assets/icons/caretLeft.svg";
import CheckCircleUrl from "../../../assets/icons/checkCircle.svg";

const PricesPageLayout = ({ children, title }) => {
  const isProd = process.env.NODE_ENV === "production";
  return (
    <Container fluid className="PricesPageLayout">
      <Helmet>
        {!isProd && (
          <meta key="robots" name="robots" content="nofollow,noindex" />
        )}
        {title && <title>{title} </title>}
      </Helmet>
      <NavigationBar search={false} />
      {children}
    </Container>
  );
};

const BreadcrumbsComponent = ({ currentName }) => (
  <Container>
    <Col xs="12" style={{ padding: "1rem 0" }}>
      <Breadcrumbs path={[]} currentName={currentName} />
    </Col>
  </Container>
);

const PageTitle = ({ children }) => (
  <Container>
    <Col xs="12" style={{ padding: "0 0 1rem 0" }}>
      <h1 className="title">{children}</h1>
    </Col>
  </Container>
);

const PageSubmit = ({ children, onClick, inactive, submitted }) => (
  <div className="fixed-submit">
    <ButtonFix
      buttoncolor={
        submitted ? "yellow--inactive" : inactive ? "ghost" : "yellow"
      }
      onClick={onClick}
      buttonsize="big"
    >
      {submitted && (
        <img
          alt="check icon"
          style={{ marginRight: "0.5rem" }}
          src={CheckCircleUrl}
        />
      )}
      {children}
    </ButtonFix>
  </div>
);

const PageNav = ({ routes, isCheese, secondary, isTopNav = false }) => {
  const { t } = useTranslation(["averagePrices"]);

  const isActive = (route, index) => {
    if (isTopNav)
      if (window.location.pathname.split("/").length > 3)
        return (
          window.location.pathname.split("/")[2] === routes[route].split("/")[2]
        );
      else return window.location.pathname === routes[route];

    if (isCheese) return index === Object.keys(routes).length - 1;

    return window.location.pathname === routes[route];
  };
  return (
    <Container>
      <Col xs="12" style={{ padding: "0 0 .5rem 0" }}>
        <div className="nav-list">
          {Object.keys(routes).map((item, index) =>
            !secondary ? (
              <ButtonFix
                buttoncolor={isActive(item, index) ? "earth" : "grey"}
                buttonsize="small"
                key={index}
                link={routes[item]}
              >
                {t(`averagePrices:navigation:${item}`)}
              </ButtonFix>
            ) : (
              <Link
                to={routes[item]}
                key={index}
                className={`secondary-nav-link ${
                  isActive(item, index) ? "active" : ""
                }`}
              >
                {t(`averagePrices:navigation:${item}`)}
              </Link>
            )
          )}
        </div>
      </Col>
    </Container>
  );
};

const FormSection = ({ children, showLoader }) => (
  <section className="report-form">
    {showLoader ? (
      <div
        style={{
          display: "flex",
          padding: "20px",
          justifyContent: "center",
        }}
      >
        <Spinner />
      </div>
    ) : (
      children
    )}
  </section>
);

const PriceReportSection = props => (
  <Container as="section" className="price-report" {...props} />
);

const PriceUnauthorizedInfo = () => {
  const { t } = useTranslation(["averagePrices"]);

  return (
    <Container as="section" className="price-report unauthorized-info">
      <Row>
        <Col xs="12">
          <div className="unauthorized-info--icon">
            <LockKey />
          </div>
          <p className="unauthorized-info--title">
            {t("averagePrices:unauthorized:title")}
          </p>
          <p className="unauthorized-info--subtitle">
            {t("averagePrices:unauthorized:subtitle")}
          </p>
          <div className="unauthorized-info--cta">
            <ButtonFix
              buttoncolor="yellow"
              buttonsize="small"
              link="/uzytkownik/login"
            >
              {t("averagePrices:unauthorized:cta")}
            </ButtonFix>
          </div>
          <div className="unauthorized-info--register">
            <Link to="/uzytkownik/login">
              {t("averagePrices:unauthorized:ctaRegister")}
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const SeoSection = ({ article }) => {
  const [isActive, setIsActive] = useState(false);
  return (
    <Container as="section" className="accordion-wrapper">
      <Row>
        <Col xs="12">
          <div
            onClick={() => setIsActive(!isActive)}
            className={`accordion ${isActive ? "open" : "closed"}`}
          >
            {article}
            <CaretLeft />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const PageFooter = ({ routing }) => {
  const { t } = useTranslation(["averagePrices"]);
  const handleItemsRender = () => {
    return Object.keys(routing).map((e, index) => {
      if (typeof routing[e] === "object") {
        if (e === "CHEESE") return "";
        return (
          <div className="prices-footer-menu--column" key={index}>
            {Object.keys(routing[e]).map((innerItem, idx) => {
              return (
                <Link
                  to={routing[e][innerItem]}
                  key={idx + index}
                  className={idx === 0 ? "header" : "item"}
                >
                  {t(
                    `averagePrices:navigation:${
                      idx === 0 ? `${innerItem}_FOOTER` : innerItem
                    }`
                  )}
                </Link>
              );
            })}
          </div>
        );
      }
      return (
        <div className="prices-footer-menu--column" key={e}>
          <Link to={routing[e]} className="header">
            {t(`averagePrices:navigation:${e}_FOOTER`)}
          </Link>
        </div>
      );
    });
  };
  handleItemsRender();
  return (
    <div className="prices-footer-menu">
      <Container as="section">
        <Row>
          <Col xs="12">
            <div className="prices-footer-menu--wrapper">
              {handleItemsRender()}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

PricesPageLayout.Breadcrumbs = BreadcrumbsComponent;
PricesPageLayout.FormSection = FormSection;
PricesPageLayout.PriceReportSection = PriceReportSection;
PricesPageLayout.PageTitle = PageTitle;
PricesPageLayout.PageNav = PageNav;
PricesPageLayout.PageSubmit = PageSubmit;
PricesPageLayout.PageUnauthorizedInfo = PriceUnauthorizedInfo;
PricesPageLayout.SeoSection = SeoSection;
PricesPageLayout.PageFooter = PageFooter;

export default PricesPageLayout;
